import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Check, Plus, Trash2, X } from 'lucide-react';
import { AlertBanner, Button, Modal, Skeleton, StatusBadge, Tabs } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/FormCard';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';
import { listEmployes } from '@/services/rh';
import { uploadFile } from '@/services/uploads';
import { toast } from '@/store/toastStore';
import {
  type DemandeAbsence, type DemandeStatut, type JourFerie, type TypeAbsence,
  ajusterSolde, annulerDemande, createDemande, createFerie, deleteDemande, deleteFerie,
  getCalendrier, getProvision, listDemandes, listFeries, listSoldes, listTypesAbsence,
  refuserDemande, validerDemande,
} from '@/services/conges';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const today = () => new Date().toISOString().slice(0, 10);
const ANNEE = new Date().getFullYear();

const STATUT_UI: Record<DemandeStatut, { label: string; status: StatusKey }> = {
  BROUILLON: { label: 'Brouillon', status: 'pending' },
  SOUMISE: { label: 'À valider', status: 'working' },
  VALIDEE: { label: 'Validée', status: 'done' },
  REFUSEE: { label: 'Refusée', status: 'stuck' },
  ANNULEE: { label: 'Annulée', status: 'pending' },
};
// Couleur d'une absence dans le calendrier, selon le type de pointage posé.
const PT_COLOR: Record<string, string> = {
  CONGE: 'bg-status-done/30 text-status-done',
  MALADIE: 'bg-amber-400/30 text-amber-700',
  PERMISSION: 'bg-status-working/25 text-status-working',
  ABSENT_JUSTIFIE: 'bg-status-stuck/20 text-status-stuck',
  ABSENT_NON_JUSTIFIE: 'bg-status-stuck/40 text-status-stuck',
};

export function CongesTab() {
  const [sub, setSub] = useState('demandes');
  const perms = usePerms('rh');
  return (
    <div>
      <Tabs
        className="mb-4"
        value={sub}
        onChange={setSub}
        tabs={[
          { key: 'demandes', label: 'Demandes' },
          { key: 'calendrier', label: 'Calendrier équipe' },
          { key: 'soldes', label: 'Soldes' },
          { key: 'feries', label: 'Jours fériés' },
          { key: 'provision', label: 'Provision 428' },
        ]}
      />
      {sub === 'demandes' && <DemandesView perms={perms} />}
      {sub === 'calendrier' && <CalendrierView />}
      {sub === 'soldes' && <SoldesView perms={perms} />}
      {sub === 'feries' && <FeriesView perms={perms} />}
      {sub === 'provision' && <ProvisionView />}
    </div>
  );
}

/* ------------------------------------------------------------- Demandes */
function joursOuvres(debut: string, fin: string, feries: Set<string>): number {
  if (!debut || !fin || fin < debut) return 0;
  let n = 0;
  for (let d = new Date(debut); d <= new Date(fin); d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().slice(0, 10);
    if (d.getDay() !== 5 && !feries.has(iso) && !feries.has(`*${iso.slice(5)}`)) n += 1; // 5 = vendredi
  }
  return n;
}

function DemandesView({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [statut, setStatut] = useState<DemandeStatut | ''>('');
  const [open, setOpen] = useState(false);
  const [employeId, setEmployeId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [dd, setDd] = useState(today());
  const [df, setDf] = useState(today());
  const [motif, setMotif] = useState('');
  const [justif, setJustif] = useState<string | null>(null);

  const { data: demandes, isLoading } = useQuery({ queryKey: ['demandes-absence', statut], queryFn: () => listDemandes(statut ? { statut } : undefined) });
  const { data: employes } = useQuery({ queryKey: ['employes'], queryFn: () => listEmployes() });
  const { data: types } = useQuery({ queryKey: ['types-absence'], queryFn: listTypesAbsence });
  const { data: feries } = useQuery({ queryKey: ['jours-feries', ANNEE], queryFn: () => listFeries(ANNEE) });
  const { data: soldes } = useQuery({ queryKey: ['soldes-conge', ANNEE], queryFn: () => listSoldes(ANNEE) });

  const feriesSet = useMemo(() => {
    const s = new Set<string>();
    (feries ?? []).forEach((f) => s.add(f.recurrent ? `*${f.jour.slice(5)}` : f.jour));
    return s;
  }, [feries]);
  const typeSel = (types ?? []).find((t) => t.id === typeId) as TypeAbsence | undefined;
  const soldeSel = soldes?.find((s) => s.employe_id === employeId);
  const nbPreview = joursOuvres(dd, df, feriesSet);
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['demandes-absence'] }); qc.invalidateQueries({ queryKey: ['soldes-conge'] }); };

  const reset = () => { setEmployeId(''); setTypeId(''); setDd(today()); setDf(today()); setMotif(''); setJustif(null); };
  const createMut = useMutation({
    mutationFn: () => createDemande({ employe_id: employeId, type_id: typeId, date_debut: dd, date_fin: df, motif: motif || null, justificatif_url: justif }),
    onSuccess: () => { invalidate(); setOpen(false); reset(); toast.success('Demande soumise.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { invalidate(); toast.success(msg); }).catch((e) => toast.error(getApiErrorMessage(e)));

  const onUpload = async (file: File) => {
    try { const up = await uploadFile(file); setJustif(up.url); toast.success('Justificatif joint.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <select className={cn(selectCls, 'w-48')} value={statut} onChange={(e) => setStatut(e.target.value as DemandeStatut | '')}>
          <option value="">Tous les statuts</option>
          <option value="SOUMISE">À valider</option>
          <option value="VALIDEE">Validées</option>
          <option value="REFUSEE">Refusées</option>
          <option value="ANNULEE">Annulées</option>
        </select>
        {perms.canModifier && <Button onClick={() => { reset(); setOpen(true); }}><Plus /> Nouvelle demande</Button>}
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (demandes ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">Aucune demande.</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Employé</th>
                <th className="px-4 py-3 label-caps">Type</th>
                <th className="px-4 py-3 label-caps">Période</th>
                <th className="px-4 py-3 text-right label-caps">Jours</th>
                <th className="px-4 py-3 label-caps">Statut</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(demandes ?? []).map((d: DemandeAbsence) => {
                const st = STATUT_UI[d.statut];
                return (
                  <tr key={d.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 font-medium text-content-primary">{d.employe_nom ?? '—'}</td>
                    <td className="px-4 py-2 text-content-secondary">{d.type?.libelle ?? '—'}</td>
                    <td className="px-4 py-2 text-content-secondary tabular-nums">{d.date_debut} → {d.date_fin}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{Number(d.nb_jours)}</td>
                    <td className="px-4 py-2"><StatusBadge status={st.status} label={st.label} /></td>
                    <td className="px-4 py-2">
                      <div className="flex items-center justify-end gap-1">
                        {perms.canModifier && d.statut === 'SOUMISE' && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" title="Valider" onClick={() => act(() => validerDemande(d.id), 'Demande validée — pointages écrits.')} aria-label="Valider"><Check className="size-4" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" title="Refuser" onClick={() => act(() => refuserDemande(d.id), 'Demande refusée.')} aria-label="Refuser"><X className="size-4" /></Button>
                          </>
                        )}
                        {perms.canModifier && (d.statut === 'VALIDEE' || d.statut === 'SOUMISE') && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Annuler" onClick={() => { if (window.confirm('Annuler cette demande ? Les pointages éventuels seront retirés.')) act(() => annulerDemande(d.id), 'Demande annulée.'); }} aria-label="Annuler"><Ban className="size-4" /></Button>
                        )}
                        {perms.canSupprimer && d.statut !== 'VALIDEE' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" title="Supprimer" onClick={() => { if (window.confirm('Supprimer cette demande ?')) act(() => deleteDemande(d.id), 'Demande supprimée.'); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onOpenChange={setOpen} title="Nouvelle demande d'absence"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={() => createMut.mutate()} loading={createMut.isPending} disabled={!employeId || !typeId}>Soumettre</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Employé">
            <select className={selectCls} value={employeId} onChange={(e) => setEmployeId(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {(employes?.items ?? []).map((e) => <option key={e.id} value={e.id}>{e.matricule} · {e.nom} {e.prenom}</option>)}
            </select>
          </FormField>
          <FormField label="Type d'absence">
            <select className={selectCls} value={typeId} onChange={(e) => setTypeId(e.target.value)}>
              <option value="">— Sélectionner —</option>
              {(types ?? []).map((t) => <option key={t.id} value={t.id}>{t.libelle}{t.remunere ? '' : ' (non rémunéré)'}</option>)}
            </select>
          </FormField>
          <FormField label="Du"><Input type="date" value={dd} onChange={(e) => setDd(e.target.value)} /></FormField>
          <FormField label="Au"><Input type="date" value={df} onChange={(e) => setDf(e.target.value)} /></FormField>
          <FormField label="Motif" className="sm:col-span-2"><Input value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Optionnel" /></FormField>
          {typeSel?.justificatif_requis && (
            <FormField label="Justificatif (requis)" className="sm:col-span-2">
              <input type="file" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); }} className="text-sm" />
              {justif && <span className="text-xs text-status-done">✓ joint</span>}
            </FormField>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-4 rounded-md border border-border bg-surface-primary p-2 text-sm">
          <span>Jours ouvrés (estimé) : <b className="tabular-nums">{nbPreview}</b></span>
          {typeSel?.impacte_solde_conge && soldeSel && (
            <span className={cn(nbPreview > soldeSel.solde ? 'text-status-stuck' : 'text-content-secondary')}>
              Solde congé {ANNEE} : <b className="tabular-nums">{Number(soldeSel.solde)}</b> j
              {nbPreview > soldeSel.solde && ' — dépassement !'}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-content-muted">Le nombre de jours définitif (hors vendredis et fériés) est recalculé par le serveur.</p>
      </Modal>
    </div>
  );
}

/* ------------------------------------------------------------ Calendrier */
function CalendrierView() {
  const [annee, setAnnee] = useState(ANNEE);
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const { data, isLoading } = useQuery({ queryKey: ['calendrier-absences', annee, mois], queryFn: () => getCalendrier(annee, mois) });

  const nbJours = new Date(annee, mois, 0).getDate();
  const jours = Array.from({ length: nbJours }, (_, i) => i + 1);
  const feriesJours = useMemo(() => new Set((data?.feries ?? []).map((f) => Number(f.jour.slice(8, 10)))), [data]);
  // Employés présents dans les absences du mois.
  const employes = useMemo(() => {
    const m = new Map<string, string>();
    (data?.absences ?? []).forEach((a) => m.set(a.employe_id, a.employe_nom));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [data]);

  const cellFor = (empId: string, jour: number) => {
    const iso = `${annee}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;
    const a = (data?.absences ?? []).find((x) => x.employe_id === empId && x.date_debut <= iso && x.date_fin >= iso);
    if (!a) return null;
    return a;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <FormField label="Mois" className="w-28"><select className={selectCls} value={mois} onChange={(e) => setMois(Number(e.target.value))}>{Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}</option>)}</select></FormField>
        <FormField label="Année" className="w-28"><Input type="number" step="1" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} /></FormField>
        <div className="flex flex-wrap gap-3 pb-2 text-xs text-content-muted">
          {Object.entries({ CONGE: 'Congé', MALADIE: 'Maladie', PERMISSION: 'Permission', ABSENT_JUSTIFIE: 'Sans solde', ABSENT_NON_JUSTIFIE: 'Injustifiée' }).map(([k, l]) => (
            <span key={k} className="inline-flex items-center gap-1"><span className={cn('inline-block size-3 rounded', PT_COLOR[k])} />{l}</span>
          ))}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : employes.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-surface-secondary p-10 text-center text-sm text-content-muted">Aucune absence ce mois-ci.</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="text-xs">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border">
                <th className="sticky left-0 z-10 bg-surface-primary px-3 py-2 text-left label-caps">Employé</th>
                {jours.map((j) => {
                  const wd = new Date(annee, mois - 1, j).getDay();
                  const off = wd === 5 || feriesJours.has(j);
                  return <th key={j} className={cn('w-7 px-0 py-2 text-center font-medium', off ? 'bg-surface-hover text-content-muted' : 'text-content-secondary')}>{j}</th>;
                })}
              </tr>
            </thead>
            <tbody>
              {employes.map(([id, nom]) => (
                <tr key={id} className="border-b border-border last:border-0">
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-surface-secondary px-3 py-1.5 font-medium text-content-primary">{nom}</td>
                  {jours.map((j) => {
                    const a = cellFor(id, j);
                    const wd = new Date(annee, mois - 1, j).getDay();
                    const off = wd === 5 || feriesJours.has(j);
                    return (
                      <td key={j} className={cn('h-7 w-7 border-l border-border/40 text-center', off && !a && 'bg-surface-hover')}>
                        {a && <span title={`${a.type_libelle} (${a.statut})`} className={cn('inline-flex h-5 w-6 items-center justify-center rounded text-[10px] font-bold', PT_COLOR[a.pointage_type ?? ''] ?? 'bg-surface-hover')}>{a.type_code}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Soldes */
function SoldesView({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [exercice, setExercice] = useState(ANNEE);
  const { data: soldes, isLoading } = useQuery({ queryKey: ['soldes-conge', exercice], queryFn: () => listSoldes(exercice) });

  const ajustMut = useMutation({
    mutationFn: ({ id, report, ajust }: { id: string; report: number; ajust: number }) => ajusterSolde(id, exercice, { report_n1: report, ajustement: ajust }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['soldes-conge', exercice] }); toast.success('Solde ajusté.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="space-y-4">
      <FormField label="Exercice" className="w-32"><Input type="number" step="1" value={exercice} onChange={(e) => setExercice(Number(e.target.value))} /></FormField>
      {isLoading ? <Skeleton className="h-64 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-3 label-caps">Employé</th>
                <th className="px-4 py-3 text-right label-caps">Droits acquis</th>
                <th className="px-4 py-3 text-right label-caps">Report N-1</th>
                <th className="px-4 py-3 text-right label-caps">Pris</th>
                <th className="px-4 py-3 text-right label-caps">Ajustement</th>
                <th className="px-4 py-3 text-right label-caps">Solde</th>
              </tr>
            </thead>
            <tbody>
              {(soldes ?? []).map((s) => (
                <tr key={s.employe_id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-medium text-content-primary">{s.employe_matricule} · {s.employe_nom}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{Number(s.droits_acquis)}</td>
                  <td className="px-4 py-2 text-right">
                    {perms.canModifier ? (
                      <input type="number" step="0.5" defaultValue={Number(s.report_n1)} className="h-8 w-16 rounded border border-border bg-surface-secondary px-1 text-right text-sm tabular-nums"
                        onBlur={(e) => { const v = Number(e.target.value); if (v !== Number(s.report_n1)) ajustMut.mutate({ id: s.employe_id, report: v, ajust: Number(s.ajustement) }); }} />
                    ) : Number(s.report_n1)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-content-secondary">{Number(s.jours_pris)}</td>
                  <td className="px-4 py-2 text-right">
                    {perms.canModifier ? (
                      <input type="number" step="0.5" defaultValue={Number(s.ajustement)} className="h-8 w-16 rounded border border-border bg-surface-secondary px-1 text-right text-sm tabular-nums"
                        onBlur={(e) => { const v = Number(e.target.value); if (v !== Number(s.ajustement)) ajustMut.mutate({ id: s.employe_id, report: Number(s.report_n1), ajust: v }); }} />
                    ) : Number(s.ajustement)}
                  </td>
                  <td className={cn('px-4 py-2 text-right font-bold tabular-nums', Number(s.solde) < 0 ? 'text-status-stuck' : 'text-content-primary')}>{Number(s.solde)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-content-muted">Droits acquis = 2,5 j/mois travaillé (plafond 30, loi 90-11). Solde = droits + report − pris + ajustement.</p>
    </div>
  );
}

/* ----------------------------------------------------------- Jours fériés */
function FeriesView({ perms }: { perms: ReturnType<typeof usePerms> }) {
  const qc = useQueryClient();
  const [annee, setAnnee] = useState(ANNEE);
  const [jour, setJour] = useState(today());
  const [libelle, setLibelle] = useState('');
  const [recurrent, setRecurrent] = useState(false);
  const { data: feries, isLoading } = useQuery({ queryKey: ['jours-feries', annee], queryFn: () => listFeries(annee) });
  const addMut = useMutation({
    mutationFn: () => createFerie({ jour, libelle, recurrent }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['jours-feries'] }); setLibelle(''); toast.success('Jour férié ajouté.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: deleteFerie, onSuccess: () => { qc.invalidateQueries({ queryKey: ['jours-feries'] }); toast.success('Supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <div className="space-y-4">
      <FormField label="Année" className="w-32"><Input type="number" step="1" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} /></FormField>
      {perms.canModifier && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface-secondary p-3">
          <FormField label="Date" className="w-40"><Input type="date" value={jour} onChange={(e) => setJour(e.target.value)} /></FormField>
          <FormField label="Libellé" className="flex-1 min-w-48"><Input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Aïd el-Fitr…" /></FormField>
          <label className="flex items-center gap-2 pb-2 text-sm text-content-secondary"><input type="checkbox" checked={recurrent} onChange={(e) => setRecurrent(e.target.checked)} /> Récurrent (date fixe)</label>
          <Button onClick={() => addMut.mutate()} loading={addMut.isPending} disabled={!libelle.trim()}><Plus className="size-4" /> Ajouter</Button>
        </div>
      )}
      {isLoading ? <Skeleton className="h-40 w-full" /> : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-surface-primary"><tr className="border-b border-border text-left"><th className="px-4 py-2 label-caps">Date</th><th className="px-4 py-2 label-caps">Libellé</th><th className="px-4 py-2 label-caps">Récurrent</th><th className="px-4 py-2" /></tr></thead>
            <tbody>
              {(feries ?? []).map((f: JourFerie) => (
                <tr key={f.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-1.5 tabular-nums">{f.jour}</td>
                  <td className="px-4 py-1.5">{f.libelle}</td>
                  <td className="px-4 py-1.5">{f.recurrent ? 'Oui' : 'Non'}</td>
                  <td className="px-4 py-1.5 text-right">{perms.canModifier && <button className="text-status-stuck" onClick={() => { if (window.confirm('Supprimer ce jour férié ?')) delMut.mutate(f.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------- Provision 428 */
function ProvisionView() {
  const [exercice, setExercice] = useState(ANNEE);
  const { data, isLoading } = useQuery({ queryKey: ['provision-conges', exercice], queryFn: () => getProvision(exercice) });
  return (
    <div className="space-y-4">
      <FormField label="Exercice" className="w-32"><Input type="number" step="1" value={exercice} onChange={(e) => setExercice(Number(e.target.value))} /></FormField>
      <AlertBanner variant="info" title="Provision pour congés payés — compte 428">
        Congés acquis non pris × salaire journalier (base/26). C'est la provision à porter au bilan / à la liasse.
      </AlertBanner>
      {isLoading || !data ? <Skeleton className="h-48 w-full" /> : (
        <>
          <div className="rounded-lg border border-border bg-surface-secondary p-4 shadow-sm">
            <p className="text-xs text-content-muted">Provision totale {exercice} ({data.nb_employes} employé{data.nb_employes > 1 ? 's' : ''})</p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums text-content-primary">{formatDZD(Number(data.total_provision))}</p>
          </div>
          {data.lignes.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-surface-primary"><tr className="border-b border-border text-left"><th className="px-4 py-2 label-caps">Employé</th><th className="px-4 py-2 text-right label-caps">Solde (j)</th><th className="px-4 py-2 text-right label-caps">Sal. journalier</th><th className="px-4 py-2 text-right label-caps">Provision</th></tr></thead>
                <tbody>
                  {data.lignes.map((l) => (
                    <tr key={l.employe_id} className="border-b border-border last:border-0">
                      <td className="px-4 py-1.5 font-medium text-content-primary">{l.matricule} · {l.nom}</td>
                      <td className="px-4 py-1.5 text-right tabular-nums">{Number(l.solde_jours)}</td>
                      <td className="px-4 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(Number(l.salaire_journalier))}</td>
                      <td className="px-4 py-1.5 text-right tabular-nums font-medium">{formatDZD(Number(l.provision))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
